import { useLocale, type Locale } from '@/lib/locale'

/**
 * Brand configuration for the Qatar demo face (alkhaleeji.scamdefense.org).
 *
 * Two presets:
 *   - `alkhaleeji` (default, public): a placeholder Qatari bank identity.
 *     "Al Khaleeji" reads as a bank without being a live one (Al Khalij
 *     Commercial Bank merged into Masraf Al Rayan in 2021).
 *   - `qnb` (private preview): the face themed as Qatar National Bank for
 *     a meeting. Never linked, never indexed; opened with `?brand=qnb`,
 *     which sticks for the tab like `?site=`. A real bank's identity on a
 *     domain it does not own is what phishing filters and the bank's own
 *     security team look for, so this stays a meeting-room mock-up.
 *
 * Colours are exposed as CSS variables under `.theme-qatar` / `.brand-*` in
 * styles/app.css; components use those tokens, never hex, so a preset swaps
 * the whole face. Spec: docs/specs/qatar-tenant.md.
 */
export type QatarBrandId = 'alkhaleeji' | 'qnb'

export type Bilingual = { en: string; ar: string }

export type QatarBrand = {
  id: QatarBrandId
  /** Short brand name, used in the wordmark and in running copy. */
  name: Bilingual
  /** Formal name for legal lines and the About page. */
  legalName: Bilingual
  /** Program name the bank gives the service. */
  program: Bilingual
  tagline: Bilingual
  /** 24/7 fraud line customers should call. */
  fraudLine: { display: string; tel: string }
  /** Where "report a scam" emails go. */
  fraudEmail: string
  /** `star`: eight-point star tile. `wordmark`: initials on a tile. */
  mark: 'star' | 'wordmark'
  colors: { primary: string; primaryDeep: string; accent: string; ink: string }
}

const BRANDS: Record<QatarBrandId, QatarBrand> = {
  alkhaleeji: {
    id: 'alkhaleeji',
    name: { en: 'Al Khaleeji', ar: 'الخليجي' },
    legalName: { en: 'Al Khaleeji Bank Q.P.S.C.', ar: 'بنك الخليجي (ش.م.ع.ق)' },
    program: { en: 'Al Khaleeji Scam Defense', ar: 'الخليجي للحماية من الاحتيال' },
    tagline: {
      en: 'Protecting our customers from fraud, before money moves.',
      ar: 'نحمي عملاءنا من الاحتيال قبل أن يتحرك المال.',
    },
    // Placeholder number and address.
    fraudLine: { display: '+974 4444 0000', tel: '+97444440000' },
    fraudEmail: 'fraud@alkhaleeji.example',
    mark: 'star',
    colors: { primary: '#8a1538', primaryDeep: '#5c0d26', accent: '#c9a24a', ink: '#1c1017' },
  },
  qnb: {
    id: 'qnb',
    name: { en: 'QNB', ar: 'QNB' },
    legalName: { en: 'Qatar National Bank (Q.P.S.C.)', ar: 'بنك قطر الوطني (ش.م.ع.ق)' },
    program: { en: 'QNB Scam Defense', ar: 'QNB للحماية من الاحتيال' },
    tagline: {
      en: 'Protecting our customers from fraud, before money moves.',
      ar: 'نحمي عملاءنا من الاحتيال قبل أن يتحرك المال.',
    },
    // QNB's published contact centre number; the email is a placeholder.
    fraudLine: { display: '+974 4440 7777', tel: '+97444407777' },
    fraudEmail: 'fraud@qnb.example',
    mark: 'wordmark',
    colors: { primary: '#5c2d91', primaryDeep: '#3a1a5e', accent: '#c8b6e6', ink: '#1a1024' },
  },
}

const OVERRIDE_KEY = 'sd_qatar_brand'

function isBrandId(v: unknown): v is QatarBrandId {
  return v === 'alkhaleeji' || v === 'qnb'
}

/**
 * `?brand=qnb` (persisted for the tab) or the default. Resolved once at
 * module load, like resolveSite: the brand never changes within a page.
 */
export function resolveQatarBrandId(): QatarBrandId {
  if (typeof window === 'undefined') return 'alkhaleeji'
  const param = new URLSearchParams(window.location.search).get('brand')?.toLowerCase()
  if (isBrandId(param)) {
    try {
      window.sessionStorage.setItem(OVERRIDE_KEY, param)
    } catch {
      /* ignore */
    }
    return param
  }
  try {
    const stored = window.sessionStorage.getItem(OVERRIDE_KEY)
    if (isBrandId(stored)) return stored
  } catch {
    /* ignore */
  }
  return 'alkhaleeji'
}

export const QATAR_BRAND: QatarBrand = BRANDS[resolveQatarBrandId()]

/** Read one side of a bilingual pair for the active language. */
export function useBrandText() {
  const locale = useLocale()
  return (pair: Bilingual) => pair[locale]
}

export function brandText(pair: Bilingual, locale: Locale): string {
  return pair[locale]
}

/**
 * The mark. Al Khaleeji: an eight-point star (a motif everywhere in Gulf
 * architecture) on a tile. QNB preset: the initials on a tile, since we do
 * not reproduce a real bank's logo. Sized by height; the wordmark scales.
 */
export function QatarMark({
  onDark = false,
  className = 'h-10',
  wordmark = true,
}: {
  onDark?: boolean
  className?: string
  wordmark?: boolean
}) {
  const locale = useLocale()
  const name = QATAR_BRAND.name[locale]
  const textColor = onDark ? '#ffffff' : QATAR_BRAND.colors.primaryDeep
  const { primary, primaryDeep, accent } = QATAR_BRAND.colors
  return (
    <span className={`inline-flex items-center gap-2.5 ${className}`} aria-label={name}>
      {QATAR_BRAND.mark === 'star' ? (
        <svg viewBox="0 0 48 48" className="h-full w-auto shrink-0" aria-hidden>
          <rect x="2" y="2" width="44" height="44" rx="12" fill={primary} />
          <path
            d="M24 9l3.6 8.4L36 14l-3.4 8.4L41 24l-8.4 1.6L36 34l-8.4-3.4L24 39l-3.6-8.4L12 34l3.4-8.4L7 24l8.4-1.6L12 14l8.4 3.4z"
            fill={accent}
          />
          <circle cx="24" cy="24" r="4.2" fill={primaryDeep} />
        </svg>
      ) : (
        <svg viewBox="0 0 48 48" className="h-full w-auto shrink-0" aria-hidden>
          <rect x="2" y="2" width="44" height="44" rx="10" fill={primary} />
          <text
            x="24"
            y="30"
            textAnchor="middle"
            fontFamily="ui-sans-serif, system-ui, sans-serif"
            fontSize="17"
            fontWeight="700"
            fill="#ffffff"
            letterSpacing="-0.5"
          >
            {QATAR_BRAND.name.en}
          </text>
        </svg>
      )}
      {wordmark && (
        <span
          className={`whitespace-nowrap font-semibold leading-none ${locale === 'ar' ? 'text-[1.35em]' : 'text-[1.15em] tracking-tight'}`}
          style={{ color: textColor }}
        >
          {name}
        </span>
      )}
    </span>
  )
}
